import { useState } from 'react'
import type { PostgrestError } from '@supabase/supabase-js'
import { rosterShift } from '../data/roster-shifts'
import type { DayException, IsoDate, ShiftCode, ShiftInstance } from '../engine/types'
import { AD_MESSAGES } from '../lib/ads'
import type { AdCallResult, MyAdvertisement } from '../lib/ads'
import { formatShortDate, formatTimeRange } from '../lib/format-date'
import type { AddFwaShiftResult } from '../lib/roster-data'
import { Button, SegmentedControl, Sheet, TextField } from '../ui'
import type { SegmentedOption } from '../ui'
import type { AdvertiseTarget } from './AdvertiseSheet'
import { ShiftCodeChip } from './ShiftCodeChip'
import { EXCEPTION_LABEL } from './ExceptionDayRow'

export interface DaySheetProps {
  /** The day being edited — exactly what was tapped in the grid. */
  date: IsoDate
  /**
   * The shifts on this day, as `effectiveRoster()` returns them for the
   * visible window. Rows with a `shiftId` (explicit `shifts` rows — FWA
   * manual or generated, or a materialised advertised shift) can be removed
   * while ROSTERED; derived block shifts cannot, and say so rather than
   * offering a dead control. Every shift can be offered for swap.
   */
  shifts: readonly ShiftInstance[]
  /**
   * The day's advertisements, if any — matched to shifts by `shiftId`. A
   * shift with an OPEN ad shows the ad and a Withdraw action instead of
   * Remove: an advertised shift can no longer be deleted, by grant design
   * (0013's own commentary), so the control is not offered.
   */
  ads: readonly MyAdvertisement[]
  /**
   * The day's exception, if it has one — `unique (user_id, date)` means at
   * most one. `id` is what the clear action deletes; without it there is
   * nothing to delete, so the clear button is not offered.
   */
  exception: DayException | null
  /** True for FWA and BLOCK_PARTIAL users (plan §1); false hides add-shift. */
  canAddFwaShift: boolean
  /**
   * `roster-data.ts`'s `addFwaShift`, owned by the screen — the typed result
   * comes back so this sheet renders the duplicate case as a sentence.
   */
  onAddFwaShift: (code: ShiftCode) => Promise<AddFwaShiftResult>
  /** `roster-data.ts`'s `deleteFwaShift`, owned by the screen. */
  onDeleteFwaShift: (shiftId: string) => Promise<{ error: PostgrestError | null }>
  /**
   * Opens the advertise composer for one of the day's shifts — the screen
   * owns the `AdvertiseSheet` state the same way it owns this sheet's.
   */
  onAdvertise: (target: AdvertiseTarget) => void
  /** `ads.ts`'s `withdrawAdvertisement`, owned by the screen — the typed result comes back as a sentence. */
  onWithdraw: (ad: MyAdvertisement) => Promise<AdCallResult>
  /**
   * `roster-data.ts`'s `upsertException`, owned by the screen — the date is
   * this sheet's `date` and comes from the tap, never from the sheet.
   * Re-marking a marked day is the caller's upsert (the `unique (user_id,
   * date)` key), never an accumulation, so this takes the day's one new kind
   * and nothing else.
   */
  onMarkException: (
    kind: DayException['kind'],
    note: string | null,
  ) => Promise<{ error: PostgrestError | null }>
  /** `roster-data.ts`'s `deleteException`, owned by the screen. */
  onClearException: () => Promise<{ error: PostgrestError | null }>
  onClose: () => void
}

const EXCEPTION_KINDS: readonly DayException['kind'][] = ['LEAVE', 'UNAVAILABLE', 'NOT_WORKING']

const KIND_OPTIONS: SegmentedOption<DayException['kind']>[] = EXCEPTION_KINDS.map((kind) => ({
  value: kind,
  label: EXCEPTION_LABEL[kind],
}))

const CODES: readonly ShiftCode[] = ['AM', 'D', 'PM', 'N']

const CODE_OPTIONS: SegmentedOption<ShiftCode>[] = CODES.map((code) => {
  const times = rosterShift(code)
  return { value: code, label: code, note: formatTimeRange(times.startMin, times.endMin) }
})

/** A roster-data failure as a sentence — never a bare code, never a no-op. */
function errorSentence(what: string, error: PostgrestError): string {
  return `Couldn't ${what} — ${error.message}`
}

/**
 * One day's editor, inside the vendored `Sheet` (plan §1 Client). It shows
 * what the day carries — shift times from the roster table, any exception —
 * and offers the Phase 3 actions (mark the day LEAVE / UNAVAILABLE /
 * NOT_WORKING with an optional note, clear the exception, add and remove an
 * FWA shift) plus Phase 4's: offer any shift for swap (which opens the
 * screen-owned `AdvertiseSheet`), and withdraw an advertised one. An
 * advertised shift shows its ad and refuses deletion — the delete grant
 * cannot reach it (0013), and a dead Remove button would be an unexplained
 * no-op.
 *
 * Every action goes through a `roster-data.ts` / `ads.ts` function the screen
 * passes in, so this component never touches Supabase and its tests need no
 * mock of it — the screen owns the modules, the sheet owns the sentences. The
 * typed results (`AddFwaShiftResult`, `AdCallResult`, `{ error }`) come back
 * to it so that a duplicate, a rejection or a failed write renders as a
 * sentence here, where the tap happened, and never as a silent no-op.
 *
 * A6, deliberately narrow: adding a shift is **date + code, nothing else** —
 * no time fields, no bespoke shift types. The date is the day being edited;
 * the only control the add section carries is the code.
 *
 * Horizon: the bound (A7) is enforced by the screen, which does not open this
 * sheet for a date outside `horizonRange(today)`. The sheet's job is only to
 * show, unmistakably, the date that was tapped.
 */
export function DaySheet({
  date,
  shifts,
  ads,
  exception,
  canAddFwaShift,
  onAddFwaShift,
  onDeleteFwaShift,
  onAdvertise,
  onWithdraw,
  onMarkException,
  onClearException,
  onClose,
}: DaySheetProps) {
  const [markKind, setMarkKind] = useState<DayException['kind']>(exception?.kind ?? 'LEAVE')
  const [noteText, setNoteText] = useState('')
  const [addCode, setAddCode] = useState<ShiftCode>('AM')
  const [status, setStatus] = useState('')

  // The screen reuses one open sheet across taps. When the tapped date
  // changes, the draft state belongs to the old day — reset it rather than
  // letting Tuesday's note land on Wednesday (React's set-state-during-render
  // reset pattern; the screen can also force it with `key={date}`).
  const [renderedDate, setRenderedDate] = useState(date)
  if (renderedDate !== date) {
    setRenderedDate(date)
    setMarkKind(exception?.kind ?? 'LEAVE')
    setNoteText('')
    setAddCode('AM')
    setStatus('')
  }

  async function handleMark() {
    // Empty text writes a database null, never a stale note — `upsertException`
    // nulls the column on re-mark.
    const note = noteText.trim() === '' ? null : noteText.trim()
    const { error } = await onMarkException(markKind, note)
    if (error) {
      setStatus(errorSentence('mark that day', error))
      return
    }
    // An exception suppresses a *derived* shift only; an explicit shift the
    // user entered stays on the day (the pinned LEAVE_OVER_EXPLICIT ruling).
    // When there is one, say so here — otherwise "Day marked as leave." reads
    // like the shift is gone when it is still on the calendar above.
    const stays =
      shifts.some((shift) => shift.source === 'EXPLICIT')
        ? " Your entered shift stays — remove it below if you don't work it."
        : ''
    setStatus(`Day marked as ${EXCEPTION_LABEL[markKind].toLowerCase()}.${stays}`)
  }

  async function handleClear() {
    const { error } = await onClearException()
    setStatus(error ? errorSentence('clear that exception', error) : 'Exception cleared.')
  }

  async function handleAdd() {
    const result = await onAddFwaShift(addCode)
    if (result.error) {
      setStatus(errorSentence('add that shift', result.error))
      return
    }
    if (result.duplicate) {
      setStatus(`You already have a ${addCode} shift on this day — nothing was added.`)
      return
    }
    setStatus(`${addCode} shift added.`)
  }

  async function handleRemove(shiftId: string) {
    const { error } = await onDeleteFwaShift(shiftId)
    setStatus(error ? errorSentence('remove that shift', error) : 'Shift removed.')
  }

  async function handleWithdraw(ad: MyAdvertisement) {
    const result = await onWithdraw(ad)
    if (result.ok) {
      setStatus('Withdrawn — the shift is back on your roster and out of every feed.')
      return
    }
    if (result.rejection !== null) {
      setStatus(AD_MESSAGES[result.rejection])
      return
    }
    setStatus(`Couldn't withdraw — ${result.error.message}`)
  }

  /** The ad sitting on one of the day's shifts, if any — only an OPEN one shows. */
  function openAdFor(shift: ShiftInstance): MyAdvertisement | null {
    if (shift.shiftId === undefined) return null
    return ads.find((ad) => ad.shiftId === shift.shiftId && ad.status === 'OPEN') ?? null
  }

  const hasDerived = shifts.some((shift) => shift.source !== 'EXPLICIT')

  return (
    <Sheet title={formatShortDate(date)} onClose={onClose}>
      <div className="sl-stack">
        {/* Exists before it has content, so a screen reader announces the
            sentence when it arrives, not just the first time. */}
        <p className="ss-sheet-status" role="status">
          {status}
        </p>

        <div className="ss-sheet-section">
          <span className="sl-label">On this day</span>
          {shifts.length === 0 && exception === null ? (
            <p className="sl-hint">Nothing on this day.</p>
          ) : null}
          {shifts.map((shift) => {
            const ad = openAdFor(shift)
            return (
              <div
                key={`${shift.date}-${shift.code}-${shift.shiftId ?? 'derived'}`}
                className="ss-day-sheet__shift"
              >
                <ShiftCodeChip code={shift.code} />
                <span className="ss-day-sheet__shift-times">
                  {formatTimeRange(
                    rosterShift(shift.code).startMin,
                    rosterShift(shift.code).endMin,
                  )}
                </span>
                {ad === null ? (
                  <Button
                    variant="ghost"
                    aria-label={`Offer the ${shift.code} shift on ${formatShortDate(date)} for swap`}
                    onClick={() =>
                      onAdvertise({
                        date: shift.date,
                        code: shift.code,
                        shiftId: shift.shiftId,
                      })
                    }
                  >
                    Offer for swap
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    aria-label={`Withdraw the ${shift.code} advertisement on ${formatShortDate(date)}`}
                    onClick={() => handleWithdraw(ad)}
                  >
                    Withdraw
                  </Button>
                )}
                {shift.source === 'EXPLICIT' && shift.shiftId && ad === null ? (
                  <Button
                    variant="ghost"
                    aria-label={`Remove the ${shift.code} shift on ${formatShortDate(date)}`}
                    onClick={() => handleRemove(shift.shiftId as string)}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            )
          })}
          {ads
            .filter((ad) => ad.status === 'OPEN')
            .map((ad) => (
              <p key={ad.id} className="sl-hint">
                The {ad.code} is advertised —{' '}
                {ad.intent === 'NEED_OFF' ? 'needs the day off' : 'open to swap'}
                {ad.noNightSwap ? ', will not swap for a night' : ''}
                {ad.note ? ` — “${ad.note}”` : ''}. Offering it again re-opens the same
                advertisement after a withdraw.
              </p>
            ))}
          {hasDerived ? (
            <p className="sl-hint">
              Roster shifts come from your block and can't be removed on their own — mark the day
              below instead.
            </p>
          ) : null}
          {exception ? (
            <p className="sl-hint">
              This day is marked <strong>{EXCEPTION_LABEL[exception.kind]}</strong>
              {exception.note ? <> — {exception.note}</> : null}.
            </p>
          ) : null}
        </div>

        {canAddFwaShift ? (
          <div className="ss-sheet-section">
            <SegmentedControl
              label="Add a shift"
              options={CODE_OPTIONS}
              value={addCode}
              onChange={setAddCode}
              hint="Times are the roster's own — this is the day and the shift, nothing else."
            />
            <Button block onClick={handleAdd}>
              Add {addCode} shift
            </Button>
          </div>
        ) : null}

        <div className="ss-sheet-section">
          <SegmentedControl
            label="Mark this day as"
            options={KIND_OPTIONS}
            value={markKind}
            onChange={setMarkKind}
            hint={
              exception
                ? 'Marking again replaces what the day is marked as — a day is one thing.'
                : undefined
            }
          />
          <TextField
            label="Note (optional)"
            value={noteText}
            onChange={setNoteText}
            hint="Shown beside the day on your calendar."
          />
          <Button block onClick={handleMark}>
            Mark this day
          </Button>
          {exception?.id ? (
            <Button variant="secondary" block onClick={handleClear}>
              Clear this exception
            </Button>
          ) : null}
        </div>
      </div>
    </Sheet>
  )
}
